import type { FormikErrors, FormikValues } from 'formik';
import type { z } from 'zod';

// Zod doesn't have a message field for whole objects, so we have to create one manually:
export function errorWhenNull(ctx: z.ErrorMapCtx, message: string): { message: string } {
  if (!ctx.data) return { message };
  return { message: ctx.defaultError };
}

/**
 * For advanced cases where we cannot simply pass the Zod schema wrapped in
 * toFormikValidationSchema to Formik. An example use case would be when we
 * want to switch Zod schemas based on changes to the Formik values object.
 *
 * This function can be called within manually within a Formik validate
 * function, passing in the specific Zod schema and Formik values that we want
 * to produce Formik errors for.
 *
 * Formik does provide some helpers for constructing errors manually, but they
 * are coupled to Yup. See validateYupSchema() and yupToFormErrors() for a
 * similar implementation.
 */
export async function manuallyConstructFormikErrors(
  schema: z.ZodSchema, // The Zod Schema
  values: FormikValues // Formik form values
): Promise<FormikErrors<FormikValues>> {
  const errors: FormikErrors<FormikValues> = {};
  try {
    await schema.parseAsync(values);
  } catch (e) {
    e.errors.forEach((err: { path: string[]; message: string }) => {
      /* Here we support nested error messages two levels deep. If we need a
         more robust solution for creating error objects deeper than two
         levels, this code could be adjusted. */
      const pathParts = err.path[0].split('.');
      if (pathParts.length === 2) {
        const pathPart0 = `${pathParts[0]}`;
        const pathPart1 = `${pathParts[1]}`;
        // Ensure that errors[pathPart0] is not undefined before adding message to it:
        if (errors[pathPart0] === undefined) {
          errors[pathPart0] = {};
        }
        // "Object is possibly 'undefined'" isn't the case here due above statement:
        errors[pathPart0][pathPart1] = err.message;
      } else {
        errors[`${err.path}`] = err.message;
      }
    });
  }
  return errors;
}

// Todo: replace with schemas in src/schemas/text.schema.ts
export const maxStringLength = 255;
export const maxStringLengthMessage = `Please enter no more than ${maxStringLength} characters`;
